import { useMemo } from 'react';

import { WalletEntryCard } from 'core/Components';
import { toTitleCase } from 'core/Utils';
import { useNavigate } from 'react-router-dom';

import { EntryElement } from '../../../hooks/queries/Ledger/types';
import { absCentsToDollar } from '../../../utils/centsToDollar';
import { getEntryPresentation } from './utils/getEntryPresentation';

type Props = {
  entry: EntryElement;
};

const DonationCard = ({ entry }: Props) => {
  const presentation = getEntryPresentation({
    entryType: entry.type,
    defaultTitle: 'One Time Donation',
  });

  return (
    <WalletEntryCard
      image={entry.image}
      title={presentation.title}
      subtitle={entry.payload?.cause?.name}
      amount={`${presentation.amountPrefix}${absCentsToDollar(entry.amount)}`}
      isFailed={presentation.isFailedOverride ?? entry.status === 'failed'}
      subText={toTitleCase(entry.status)}
    />
  );
};

const PurchaseCard = ({ entry }: Props) => {
  const navigate = useNavigate();

  const title = useMemo(() => {
    return entry.title ? `${entry.title} Purchase` : 'Purchase';
  }, [entry.title]);

  const presentation = getEntryPresentation({
    entryType: entry.type,
    defaultTitle: title,
  });

  return (
    <WalletEntryCard
      image={entry.image}
      title={presentation.title}
      subtitle={entry.subtitle}
      amount={`${absCentsToDollar(entry.cashback_amount)} Back`}
      isFailed={entry.status === 'failed'}
      subText={toTitleCase(entry.status)}
      onClick={() => navigate(entry.provider_id)}
    />
  );
};

const TransferCard = ({ entry }: Props) => {
  const presentation = getEntryPresentation({
    entryType: entry.type,
    defaultTitle: 'Transfer',
  });

  return (
    <WalletEntryCard
      image={entry.image}
      title={presentation.title}
      subtitle={toTitleCase(entry.payload.account_type ?? '')}
      amount={`${presentation.amountPrefix}${absCentsToDollar(entry.amount)}`}
      isFailed={presentation.isFailedOverride ?? entry.status === 'failed'}
      subText={toTitleCase(entry.status)}
    />
  );
};

const EarningCard = ({ entry }: Props) => {
  if (entry.payout) {
    return <TransferCard entry={entry} />;
  }
  if (entry.card_link) {
    return <PurchaseCard entry={entry} />;
  }
  return <DonationCard entry={entry} />;
};

export { EarningCard };
